import ReactGA from "react-ga";
import Aegis from "aegis-web-sdk";

export function initializeGA(sessionId, spaceKey) {
  ReactGA.initialize(
    [
      {
        trackingId: "UA-65952334-7",
        gaOptions: {
          userId: sessionId,
          clientId: `${spaceKey}`,
          siteSpeedSampleRate: 100,
        },
      },
      {
        trackingId: "UA-182121566-1",
        gaOptions: {
          userId: sessionId,
          clientId: `${spaceKey}`,
          siteSpeedSampleRate: 100,
        },
      },
    ],
    {
      titleCase: false,
      alwaysSendToDefaultTracker: false,
    }
  );
}

export function logRestTime(spaceKey) {
  ReactGA.event({
    category: "cs_ide/points and limit",
    action: `没有剩余时长`,
    label: `${spaceKey}`,
    value: 0,
  });
}

export function logRestPoint(spaceKey, limit) {
  ReactGA.event({
    category: "cs_ide/points and limit",
    action: `没有剩余积分`,
    label: `${spaceKey}`,
    value: limit || 0,
  });
}

export function logFullScreen(spaceKey) {
  ReactGA.event({
    category: "cs_ide/fullScreen",
    action: "click fullScreen icon",
    label: `${spaceKey}`,
  });
}

export function logReload(spaceKey) {
  ReactGA.event({
    category: "cs_ide/reload",
    action: "reloadCloudStudio",
    label: `${spaceKey}`,
  });
}

export function logTemplate(spaceKey) {
  ReactGA.event({
    category: "get template",
    action: "点击自定义模板",
    label: `${spaceKey}`,
  });
}

export function logDeployTemplate(spaceKey, data) {
  ReactGA.event({
    category: data.event === "deploy" ? "deploy template" : "reissue template",
    action: data.event === "deploy" ? "发布模板" : "再次发布模板",
    label: `${spaceKey}`,
  });
}

export function logPoints(points, limit, groupId, username) {
  ReactGA.event({
    category: "cs_ide/points and limit",
    action: `已使用：${
      points.quota - points.remain || 0
    } 积分，剩余时长：${limit}`,
    label: `${groupId}/${username}`,
    value: limit || 0,
  });
}

export function logTtyOk(spaceKey, TTYTime, StartTime) {
  ReactGA.event({
    category: "cs_ide/tty_ok",
    action: "load",
    label: `${spaceKey}`,
    value: Math.round((TTYTime - StartTime) / 1000),
  });
  ReactGA.timing({
    category: "cs_ide/tty_ok",
    variable: "load",
    value: TTYTime - StartTime,
    label: `${spaceKey}`,
  });
}

export function logLoaderInit(spaceKey) {
  ReactGA.event({
    category: "ws_loader/init",
    action: "is running",
    label: `${spaceKey}`,
  });
}

export function logIdeInit(spaceKey) {
  ReactGA.event({
    category: "cs_ide/init",
    action: "is running",
    label: `${spaceKey}`,
  });
}

export function logLoaderTime(status, spaceKey, EndTime, StartTime) {
  ReactGA.event({
    category: "cs_ide/ws_loader_time",
    action: status === "RUNNING" ? "启动" : "创建",
    label: `${spaceKey}`,
    value: Math.round((EndTime - StartTime) / 1000),
  });
  ReactGA.timing({
    category: "cs_ide/ws_loader_time",
    variable: `${spaceKey}`,
    value: EndTime - StartTime,
    label: status === "RUNNING" ? "启动" : "创建",
  });
}

export function logIdeCannotEnter(spaceKey) {
  ReactGA.event({
    category: "cs_ide/cannot_enter",
    action: "cannot enter",
    label: `${spaceKey}`,
  });
}

// 接入Aegis
let _isDevelopment = process.env.NODE_ENV === "development";
const _startTime = Date.now();
const OPEN_WORKSPACE_EVENT = "custom_event/open_workspace";
const CLUB_OPEN_WORKSPACE_EVENT = 'custom_event/club/open_workspace';

const AEGIS_ID_TESTING = "gQlQWfgna8znyZ3E2G";
const AEGIS_ID_SAAS = "JjgGKclJP1E0pRaXqb";
const AEGIS_ID_QCLOUD = "xEKagHQqVOD9E7jRZq";

let publicConfig = {};

const getId = () => {
  const { environment } = publicConfig;
  if (_isDevelopment || !environment || environment !== 'production') {
    return AEGIS_ID_TESTING; // 测试 id
  }
  const hostname = window.location.hostname;
  return hostname.includes('cloudstudio')
    ? AEGIS_ID_SAAS
    : hostname.includes('tencent')
      ? AEGIS_ID_QCLOUD
      : null;
};

const eventStr = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('tencent')) {
    return OPEN_WORKSPACE_EVENT;
  }
  const { cloudStudioUrl = '' } = publicConfig;
  const cloudStudioHost = cloudStudioUrl.replace(/^https?:\/\//, '');
  if (hostname === cloudStudioHost || hostname.startsWith('club.')) {
    return CLUB_OPEN_WORKSPACE_EVENT;
  }
  return OPEN_WORKSPACE_EVENT;
};

export let aegis = null;

export function initializeAegis(data = {}) {
  publicConfig = data;
  const id = getId();
  if (!id) {
    return;
  }
  aegis = new Aegis({
    id, // 上报 id
    env: process.env.NODE_ENV,
    reportApiSpeed: true, // 接口测速
    reportAssetSpeed: true, // 静态资源测速
    spa: true, // spa 应用页面跳转的时候开启 pv 计算
    websocketHack: true, // 监控界面的websocket 错误
    api: {
      injectTraceHeader: "traceparent",
      injectTraceUrls: [/cloudstudio.net/, /cloud.tencent.com/],
    },
  });
}

export const once = (fn) => {
  let called = false;
  return function (...args) {
    if (called) return;
    called = true;
    return fn.apply(this, args);
  };
};

export const logWorkSpaceSpeed = once(
  (incompatibleType, startupType, error) => {
    const prefix = [incompatibleType, startupType].filter(i=>i).join('_');
    logSpeed(prefix, error);
  }
);

export const logSpeed = (extMsg, error) => {
  const url = eventStr();
  const duration = Date.now() - _startTime;
  console.log(
    `CUSTOM_EVENT logSpeed aegis_id=${aegis?.config?.id} event=${url}, duration=${duration}ms, ext3=${extMsg}, error=${error}`
  );
  aegis?.speedLogPipeline({
    url,
    isHttps: window.location.protocol === "https:",
    method: "get",
    type: "fetch",
    duration,
    status: !error ? 200 : 500, // HTTP Code 成功率
    isErr: !error ? 0 : 1, // retcode 成功路
    ext1: !error ? "success" : error.message || error.msg || error,
    ext2: window?.location?.href,
    ext3: extMsg || "",
  });
};
